import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { DateTimeFormatterAndParser } from "abweb/utils/date/DateTimeStructs";
import { DateUtilsLuxonInternals } from "abweb/utils/date/internal/DateUtilsLuxonInternals";
import { DateTime } from "luxon";
/**
 * Internal helper class that reasons about Intl.DateTimeFormatOptions for localizing dates/times.
 * Do NOT use direct - use DateTimeUtils methods instead.
 */
export class DateUtilsIntlInternals {
  constructor() {}

  /**
   * @return the luxon parse string corresponding to the intlOptions value for the current locale.
   * The returned parse string can be used to parse a localized date.
   */
  static getLocalizedParseFormat(intlOptions) {
    let localeFormat = DateTime.parseFormatForOpts(intlOptions, DateUtilsLuxonInternals.luxonLocaleOpts);
    if (!localeFormat) {
      throw new Error("Can not determine parse format for Intl.DateTimeFormatOptions: " + JSON.stringify(intlOptions));
    }
    return DateUtilsIntlInternals.fixIntlParseFormat(localeFormat);
  }

  // Separate out for easier testing
  static fixIntlParseFormat(localeFormat) {
    let fixLocaleFormat = localeFormat;
    // For intl dates, it returns parse format of 'yyyyy' for the year, which accepts 4-6 char year as input.
    // However, ab initio has traditionally only supported 4 char year, so conservatively keep the ab initio behavior
    // (and do not know of a use case where customer would need anything more than 4 digit years).
    // It is also confusing if a user sees 'yyyyy' in an input placeholder string, as they expect only yyyy
    if (fixLocaleFormat.indexOf("yyyyy") >= 0 && fixLocaleFormat.indexOf("yyyyyy") < 0) {
      fixLocaleFormat = fixLocaleFormat.replace("yyyyy", "yyyy"); // do not convert 6 char 'yyyyyy'
    }
    // Work around datetime bug around 24 hr time, where observed that spanish DATETIME_MEDIUM returned 'd/M/yyyy, h:m'
    // when should have been 'd/M/yyyy, H:m'   (which also broke subsequent generated kendo parse format)
    // // en-US time has ' a' suffix while ch-ZN has 'a' prefix before time (aH:mm... or aHH:mm...)
    fixLocaleFormat = fixLocaleFormat.endsWith("a") || fixLocaleFormat.indexOf("aH") >= 0 || fixLocaleFormat.indexOf("ah") >= 0 ? fixLocaleFormat.replace("HH:", "hh:").replace("H:", "h:") : fixLocaleFormat.replace("hh:", "HH:").replace("h:", "H:");

    // console.log(`***fixIntlParseFormat('${localeFormat}') => '${fixLocaleFormat}' *****`)
    return fixLocaleFormat;
  }
  static buildIntlDateTimeFormatter(options, rmComma = false) {
    return new IntlDateTimeFormatter(options, rmComma);
  }
  static buildIntlParser(options, rmComma = false) {
    return new IntlDateTimeParser(options, rmComma);
  }
  static buildIntlDateTimeFormatterAndParser(options, rmComma = false) {
    const formatter = DateUtilsIntlInternals.buildIntlDateTimeFormatter(options, rmComma);
    const parser = DateUtilsIntlInternals.buildIntlParser(options, rmComma);
    return new DateTimeFormatterAndParser(formatter, parser);
  }
}

/*private*/
class IntlDateTimeFormatter {
  constructor(intlOptions, rmComma = false) {
    // public readonly format: DateFormatter;
    _defineProperty(this, "rawFormatter", void 0);
    this.intlOptions = intlOptions;
    this.rmComma = rmComma;
    // Curry away newing up the Intl.DateTimeFormat formatter, as that is a hundred times slower than formatting the date
    this.rawFormatter = new Intl.DateTimeFormat(DateUtilsLuxonInternals.datetimeLocale, intlOptions);
  }
  format(val) {
    if (val) {
      // If rmComma is true, the first comma (specifically ", ") in the formatted output is removed
      // Simple way to remove commas, but could also use more complex formatToParts.  For more details, see:
      //  https://stackoverflow.com/questions/67177522/remove-comma-from-date-which-is-generated-from-intl-datetimeformat
      return this.rmComma ? this.rawFormatter.format(val).replace(/, /g, " ") : this.rawFormatter.format(val);
    }
    // Note: if don't short circuit on null|undefined, the raw intl formatter defaults to dec 31 ...  which is not what anyone expects
    // Luxon DateTimeFormatter also similarly throws Error on null or undefined
    throw new Error("Can not format a date as received a null or undefined value.");
  }
}

/*private*/
class IntlDateTimeParser {
  constructor(intlOptions, rmComma = false) {
    _defineProperty(this, "parseContext", void 0);
    _defineProperty(this, "parseDelegator", void 0);
    this.intlOptions = intlOptions;
    this.rmComma = rmComma;
    // Intl spec does not support parsing.
    // So get equivalent luxon parse format for given intlOptions, then build corresponding luxon parser
    const luxonParseFormat = DateUtilsIntlInternals.getLocalizedParseFormat(intlOptions);
    const dateTimeOptions = Object.hasOwn(intlOptions, "timeZone") ? {
      locale: DateUtilsLuxonInternals.datetimeLocale,
      zone: intlOptions.timeZone // explicitly specified (and can be undefined!)
    }
    // do NOT provide 'zone: undefined' as prevents underlying default from kicking in.
    // If don't want underlying default, then can set intlOptions.timeZone = undefined
    : {
      locale: DateUtilsLuxonInternals.datetimeLocale
    };
    this.parseDelegator = DateUtilsLuxonInternals.buildDateTimeParser(luxonParseFormat, dateTimeOptions);
    this.parseContext = this.parseDelegator.parseContext;
  }
  parse(val) {
    // Special handling around 'rmComma' workflows
    // Add back first comma so that parser will work (as intl options expects parse string to have the comma)
    const fixedVal = val && this.rmComma ? val.replace(" ", ", ") : val;
    return this.parseDelegator.parse(fixedVal);
  }
}
//# sourceMappingURL=DateUtilsIntlInternals.js.map
